import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDebouncedValue } from '@/hooks/urlState';

import type { WarehouseApiError } from '../api/warehouse.api';
import type { Paginated } from '../types';
import { useLoad } from './useLoad';

/** Rows per picker page (first page, every search, every "more"). */
export const PICKER_PAGE_SIZE = 20;
/** Quiet time before a typed search goes to the server. */
export const PICKER_SEARCH_DEBOUNCE_MS = 300;

const NO_ROWS: never[] = [];

export interface PagedPicker<T> {
  /** The live input (what the user typed). */
  input: string;
  setSearch: (value: string) => void;
  /** The user is searching (typed something). */
  searching: boolean;
  /** Rows loaded so far for the current search (first page + "more"). */
  rows: T[];
  hasMore: boolean;
  loadMore: () => void;
  /** First page, a search or "more" is pending or on the wire. */
  loading: boolean;
  /** The first page / search failed — the list is unknown, NOT empty. */
  error: WarehouseApiError | null;
  /** Ask the first page again (after a failure). */
  reload: () => void;
}

export interface PageRequest {
  search: string | null;
  page: number;
  limit: number;
}

interface MorePages<T> {
  scope: string;
  rows: T[];
  /** Last page loaded, and how many there are. */
  page: number;
  lastPage: number;
}

/**
 * One server-side picker list: the first page once `enabled` (the picker was
 * reached), `search` debounced when the user types — a newer search aborts the
 * older request, so a slow stale answer never replaces the current rows — and
 * `loadMore` for the following pages (appended, one request at a time, a row a
 * later page repeats is dropped). `scope` is everything that selects the list
 * besides the search (hub, level, parent, SKU …): a new scope starts over.
 * `searchOnly` asks nothing for the empty search (the caller already holds
 * that first page). `pageSize` is how many rows one request brings (default
 * PICKER_PAGE_SIZE); a picker that opens on a list rather than a blank asks
 * for a bigger first page. Never loads the whole list.
 */
export function usePagedPicker<T>({
  scope: base,
  enabled,
  searchOnly = false,
  pageSize = PICKER_PAGE_SIZE,
  keyOf,
  fetchPage,
}: {
  scope: string;
  enabled: boolean;
  searchOnly?: boolean;
  pageSize?: number;
  keyOf: (row: T) => string;
  fetchPage: (req: PageRequest, signal: AbortSignal) => Promise<Paginated<T>>;
}): PagedPicker<T> {
  const [input, setSearch] = useState('');
  const typed = input.trim();
  const debounced = useDebouncedValue(typed, PICKER_SEARCH_DEBOUNCE_MS);
  // Clearing the input is not debounced: back to the first page at once.
  const term = typed ? debounced : '';
  const scope = `${base}|${term}`;
  // What is actually asked: nothing for an empty search when `searchOnly`.
  const active = enabled && (!searchOnly || !!term);

  // The latest closures, read by the (aborted-on-scope-change) requests.
  const latest = useRef({ keyOf, fetchPage });
  latest.current = { keyOf, fetchPage };

  const first = useLoad(
    async (signal) => ({
      scope,
      page: await latest.current.fetchPage(
        { search: term || null, page: 1, limit: pageSize },
        signal
      ),
    }),
    [scope],
    active
  );
  // The previous scope's rows stay in useLoad while the next loads — never show them.
  const firstPage = first.data?.scope === scope ? first.data.page : null;

  const [more, setMore] = useState<MorePages<T> | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const moreRequest = useRef<AbortController | null>(null);
  // A new scope or leaving drops the pending "more".
  useEffect(
    () => () => {
      moreRequest.current?.abort();
      moreRequest.current = null;
      setLoadingMore(false);
    },
    [scope, active]
  );
  const current = more?.scope === scope ? more : null;

  const rows = useMemo(() => {
    if (!firstPage) return NO_ROWS as T[];
    if (!current) return firstPage.data;
    const seen = new Set(firstPage.data.map(keyOf));
    return [
      ...firstPage.data,
      ...current.rows.filter((r) => !seen.has(keyOf(r))),
    ];
  }, [firstPage, current, keyOf]);
  const loadedPage = current?.page ?? firstPage?.pagination.page ?? 1;
  const lastPage = current?.lastPage ?? firstPage?.pagination.lastPage ?? 1;
  const hasMore = !!firstPage && loadedPage < lastPage;

  const loadMore = useCallback(() => {
    if (!active || !hasMore || moreRequest.current) return;
    const controller = new AbortController();
    moreRequest.current = controller;
    setLoadingMore(true);
    latest.current
      .fetchPage(
        {
          search: term || null,
          page: loadedPage + 1,
          limit: pageSize,
        },
        controller.signal
      )
      .then((res) => {
        if (controller.signal.aborted) return;
        setMore((prev) => {
          const kept = prev?.scope === scope ? prev.rows : (NO_ROWS as T[]);
          const seen = new Set(kept.map(latest.current.keyOf));
          return {
            scope,
            rows: [
              ...kept,
              ...res.data.filter((r) => !seen.has(latest.current.keyOf(r))),
            ],
            page: res.pagination.page,
            lastPage: res.pagination.lastPage,
          };
        });
      })
      // A failed "more" leaves the rows as they are; scrolling again retries.
      .catch(() => undefined)
      .finally(() => {
        if (controller.signal.aborted) return;
        if (moreRequest.current === controller) moreRequest.current = null;
        setLoadingMore(false);
      });
  }, [active, hasMore, term, loadedPage, scope, pageSize]);

  return {
    input,
    setSearch,
    searching: !!typed,
    rows,
    hasMore,
    loadMore,
    // Unknown ≠ empty: the first answer (or its failure) is still to come.
    loading:
      (!!typed && typed !== debounced) ||
      (active && (first.loading || (!firstPage && !first.error))) ||
      loadingMore,
    error: active ? first.error : null,
    reload: first.reload,
  };
}
